import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LocationContext = createContext();

export const useLocation = () => {
    const context = useContext(LocationContext);
    if (!context) {
        throw new Error('useLocation must be used within a LocationProvider');
    }
    return context;
};

export const LocationProvider = ({ children }) => {
    const [location, setLocation] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [permissionStatus, setPermissionStatus] = useState('prompt'); // 'granted', 'denied', 'prompt'
    const [watchId, setWatchId] = useState(null);

    // Check if geolocation is supported
    const isSupported = 'geolocation' in navigator;

    // Check permission status
    const checkPermission = useCallback(async () => {
        if (!isSupported) {
            setError('Geolocation is not supported by this browser');
            return;
        }

        try {
            const permission = await navigator.permissions.query({ name: 'geolocation' });
            setPermissionStatus(permission.state);

            // Listen for permission changes
            permission.onchange = () => {
                setPermissionStatus(permission.state);
            };
        } catch (err) {
            console.warn('Could not check geolocation permission:', err);
        }
    }, [isSupported]);

    // Get current position once
    const getCurrentLocation = useCallback((options = {}) => {
        return new Promise((resolve, reject) => {
            if (!isSupported) {
                const error = new Error('Geolocation is not supported');
                setError(error.message);
                reject(error);
                return;
            }

            setIsLoading(true);
            setError(null);

            const defaultOptions = {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 300000, // 5 minutes
                ...options
            };

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const locationData = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        altitude: position.coords.altitude,
                        heading: position.coords.heading,
                        speed: position.coords.speed,
                        timestamp: position.timestamp
                    };

                    setLocation(locationData);
                    setIsLoading(false);
                    resolve(locationData);
                },
                (error) => {
                    let errorMessage;
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            errorMessage = 'Location access denied by user';
                            setPermissionStatus('denied');
                            break;
                        case error.POSITION_UNAVAILABLE:
                            errorMessage = 'Location information unavailable';
                            break;
                        case error.TIMEOUT:
                            errorMessage = 'Location request timed out';
                            break;
                        default:
                            errorMessage = 'An unknown error occurred while retrieving location';
                            break;
                    }

                    setError(errorMessage);
                    setIsLoading(false);
                    reject(new Error(errorMessage));
                },
                defaultOptions
            );
        });
    }, [isSupported]);

    // Watch position for real-time updates
    const watchLocation = useCallback((options = {}) => {
        if (!isSupported) {
            setError('Geolocation is not supported');
            return null;
        }

        if (watchId) {
            // Already watching
            return watchId;
        }

        setError(null);

        const defaultOptions = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 60000, // 1 minute for watch
            ...options
        };

        const id = navigator.geolocation.watchPosition(
            (position) => {
                const locationData = {
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    altitude: position.coords.altitude,
                    heading: position.coords.heading,
                    speed: position.coords.speed,
                    timestamp: position.timestamp
                };

                setLocation(locationData);
                setIsLoading(false);
            },
            (error) => {
                let errorMessage;
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Location access denied';
                        setPermissionStatus('denied');
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'Location unavailable';
                        break;
                    case error.TIMEOUT:
                        errorMessage = 'Location request timed out';
                        break;
                    default:
                        errorMessage = 'Unknown location error';
                        break;
                }

                setError(errorMessage);
                setIsLoading(false);
            },
            defaultOptions
        );

        setWatchId(id);
        setIsLoading(true);
        return id;
    }, [isSupported, watchId]);

    // Stop watching location
    const stopWatching = useCallback(() => {
        if (watchId && isSupported) {
            navigator.geolocation.clearWatch(watchId);
            setWatchId(null);
            setIsLoading(false);
        }
    }, [watchId, isSupported]);

    // Clear location data
    const clearLocation = useCallback(() => {
        setLocation(null);
        setError(null);
        stopWatching();
    }, [stopWatching]);

    // Calculate distance between two points (Haversine formula)
    const calculateDistance = useCallback((lat1, lon1, lat2, lon2) => {
        const R = 6371; // Earth's radius in kilometers
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c; // Distance in kilometers
    }, []);

    // Get formatted address from coordinates (requires additional API)
    const getAddressFromCoords = useCallback(async (latitude, longitude) => {
        // This would require a geocoding service like Google Maps Geocoding API
        // For now, returning a placeholder
        return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
    }, []);

    // Initialize permission check
    useEffect(() => {
        checkPermission();
    }, [checkPermission]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopWatching();
        };
    }, [stopWatching]);

    const value = {
        // State
        location,
        isLoading,
        error,
        permissionStatus,
        isSupported,
        isWatching: !!watchId,

        // Methods
        getCurrentLocation,
        watchLocation,
        stopWatching,
        clearLocation,
        checkPermission,
        calculateDistance,
        getAddressFromCoords
    };

    return (
        <LocationContext.Provider value={value}>
            {children}
        </LocationContext.Provider>
    );
};